import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Send, RefreshCw, Lock, CheckCircle2, ArrowLeft } from "lucide-react";

/**
 * Public verification landing page. Flow:
 *   1. Discord-linked user hits the page (signed-in required)
 *   2. Solves a 1-question math captcha
 *   3. We DM them a 6-digit code
 *   4. They paste the code → we mark `is_verified` and (re)assign Members role
 */
export default function Verify() {
  const { user, loading, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [captcha, setCaptcha] = useState(null);        // { id, question }
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [stage, setStage] = useState("captcha");        // captcha | code | done
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const loadCaptcha = async () => {
    try {
      const { data } = await api.get("/user/verify/captcha");
      setCaptcha(data);
      setCaptchaAnswer("");
    } catch {
      toast.error("Couldn't load captcha");
    }
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/user/verify/status");
      setStatus(data);
      if (data.is_verified) setStage("done");
      else if (data.has_pending_code) setStage("code");
      else setStage("captcha");
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (user?.id) {
      loadStatus();
      loadCaptcha();
    }
  }, [user?.id]);

  if (loading) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_banned) return <Navigate to="/banned" replace />;

  const sendCode = async (e) => {
    e.preventDefault();
    if (!captcha || !captchaAnswer.trim()) return toast.error("Answer the captcha first.");
    setSending(true);
    try {
      await api.post("/user/verify/start", {
        captcha_id: captcha.id,
        captcha_answer: captchaAnswer.trim(),
      });
      toast.success("Code sent — check your Discord DMs.");
      setStage("code");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send code");
      // Refresh captcha so they can retry
      loadCaptcha();
    } finally { setSending(false); }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) return toast.error("Enter the 6-digit code from your DM.");
    setConfirming(true);
    try {
      await api.post("/user/verify/confirm", { code: code.trim() });
      toast.success("You're verified!");
      await refresh();
      setStage("done");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setConfirming(false); }
  };

  return (
    <div className="max-w-[820px] mx-auto px-6 lg:px-12 py-24" data-testid="verify-page">
      <Link to="/profile" className="btn-ghost text-xs mb-6 inline-flex" data-testid="verify-back-link">
        <ArrowLeft size={12} /> Back to Profile
      </Link>

      <div className="mb-10">
        <div className="label-eyebrow mb-2 text-[var(--ff-red)] flex items-center gap-2">
          <ShieldCheck size={12} /> ▮ VERIFICATION
        </div>
        <h1 className="font-display text-4xl lg:text-5xl leading-none" data-testid="verify-heading">
          {stage === "done" ? "YOU'RE VERIFIED." : "PROVE YOU'RE HUMAN."}
        </h1>
        <p className="text-[var(--ff-text-dim)] text-sm mt-3 font-mono-ui max-w-xl">
          {stage === "done"
            ? "Your account is confirmed and the Members role has been assigned on Discord."
            : "Two quick steps: solve a captcha, then confirm a 6-digit code we DM you on Discord."}
        </p>
      </div>

      {!user.discord_id && (
        <div className="surface-card p-6 border-yellow-500/40 text-yellow-200 text-sm font-mono-ui" data-testid="verify-no-discord">
          You need to link your Discord account first. <Link to="/login" className="underline">Sign in with Discord</Link>.
        </div>
      )}

      {user.discord_id && stage === "done" && (
        <div className="surface-card p-8 text-center" data-testid="verify-done-panel">
          <CheckCircle2 size={64} className="mx-auto text-green-400 mb-4" />
          <div className="font-display text-2xl mb-2">Verified ✓</div>
          <div className="text-[var(--ff-text-dim)] text-sm font-mono-ui mb-6">
            {status?.verified_at && (
              <>Verified on {new Date(status.verified_at).toLocaleDateString()} — welcome aboard.</>
            )}
          </div>
          <div className="flex justify-center gap-3">
            <Link to="/watchlist" className="btn-primary" data-testid="verify-goto-watchlist">Go to Watchlist</Link>
            <Link to="/profile" className="btn-ghost" data-testid="verify-goto-profile">Back to Profile</Link>
          </div>
        </div>
      )}

      {user.discord_id && stage === "captcha" && captcha && (
        <form onSubmit={sendCode} className="surface-card p-8 space-y-6" data-testid="verify-captcha-form">
          <div className="flex items-center gap-3 pb-4 border-b border-[var(--ff-border)]">
            <div className="w-10 h-10 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-sm">1</div>
            <div>
              <div className="font-display text-xl">Step 1 · Captcha</div>
              <div className="text-[var(--ff-text-dim)] text-xs font-mono-ui uppercase tracking-[0.15em]">Prove you&apos;re not a bot.</div>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{captcha.question}</label>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                placeholder="Answer"
                className="flex-1 px-4 py-3 text-sm"
                data-testid="verify-captcha-input"
                autoFocus
              />
              <button type="button" onClick={loadCaptcha} className="btn-ghost text-xs" data-testid="verify-captcha-refresh" title="New question">
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
          <div className="pt-4 border-t border-[var(--ff-border)] flex items-center justify-between">
            <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] flex items-center gap-2">
              <Lock size={11} /> We&apos;ll DM the 6-digit code to your Discord.
            </div>
            <button type="submit" disabled={sending} className="btn-primary" data-testid="verify-send-btn">
              <Send size={14} /> {sending ? "Sending…" : "Send Code"}
            </button>
          </div>
        </form>
      )}

      {user.discord_id && stage === "code" && (
        <form onSubmit={confirmCode} className="surface-card p-8 space-y-6" data-testid="verify-code-form">
          <div className="flex items-center gap-3 pb-4 border-b border-[var(--ff-border)]">
            <div className="w-10 h-10 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-sm">2</div>
            <div>
              <div className="font-display text-xl">Step 2 · Enter Code</div>
              <div className="text-[var(--ff-text-dim)] text-xs font-mono-ui uppercase tracking-[0.15em]">Check your Discord DMs for a 6-digit code.</div>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Verification Code</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="w-full px-4 py-4 text-2xl font-mono-ui text-center tracking-[0.5em]"
              data-testid="verify-code-input"
              autoFocus
            />
            <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mt-2">
              Expires in 10 minutes · 5 attempts max
            </div>
          </div>
          <div className="pt-4 border-t border-[var(--ff-border)] flex items-center justify-between">
            <button
              type="button"
              onClick={async () => { setStage("captcha"); setCode(""); await loadCaptcha(); }}
              className="btn-ghost text-xs"
              data-testid="verify-resend-btn"
            >
              <RefreshCw size={12} /> Restart
            </button>
            <button type="submit" disabled={confirming} className="btn-primary" data-testid="verify-confirm-btn">
              <ShieldCheck size={14} /> {confirming ? "Verifying…" : "Confirm"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
